import { z } from "zod";
import { CreditRefundReasonSchema } from "./credits.js";
import { SupportedCurrencyCodesSchema } from "./currencies.js";
import { ItemSplitSchema } from "./expenses.js";
/**
 * Receipt scanning: a client uploads a receipt image to S3 via a presigned
 * ticket, then asks the API to OCR (Textract) and structure (Bedrock/Gemma)
 * it into a best-effort expense draft. The draft prefills the normal
 * create-expense form — scanning never writes an expense directly. See
 * docs/receipt-scanning.md.
 */
/**
 * Content types accepted for a receipt upload. Images use synchronous Textract
 * detection; PDFs use Textract's asynchronous multi-page document detection.
 */
export const ReceiptContentTypeSchema = z.enum([
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/webp",
    "application/pdf",
]);
/** Body of `POST /scans/uploads`. */
export const CreateUploadTicketRequestSchema = z.strictObject({
    contentType: ReceiptContentTypeSchema,
    /** File size in bytes; capped server-side to reject oversized uploads. */
    byteSize: z.number().int().positive(),
    /**
     * SHA-256 of the exact bytes that will be uploaded. New clients provide it
     * so the server can reuse an identical upload; optional keeps older mobile
     * binaries compatible with the endpoint.
     */
    contentHash: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
});
/**
 * Response of `POST /scans/uploads`: a presigned PUT the client uploads the
 * image bytes to, plus the server-owned object key to pass back to the scan
 * endpoint. The client never chooses the key.
 */
export const UploadTicketSchema = z.strictObject({
    /** Absent when an identical, verified object is already present. */
    uploadUrl: z.string().url().optional(),
    objectKey: z.string().min(1),
    expiresAt: z.string().datetime().optional(),
    uploadRequired: z.boolean(),
    /** Base64 SHA-256 checksum that must accompany a new S3 upload. */
    checksumSha256: z.string().optional(),
    /**
     * Identifies this user-initiated scan attempt. Passing it to the job endpoint
     * makes HTTP retries idempotent without making a later selection of the same
     * content free.
     */
    attemptId: z.string().uuid(),
});
/**
 * A single drafted line item. Best-effort: only `label` is required so a
 * partially-recognised receipt still produces usable rows. `cost` is in minor
 * units (cents/pence) when present; `split` defaults to EQUAL client-side.
 */
export const DraftItemSchema = z.strictObject({
    label: z.string().min(1).max(200),
    /** Signed minor units; receipt discounts are negative. */
    cost: z.number().int().optional(),
    split: ItemSplitSchema.optional(),
    category: z.enum(["FOOD", "DRINK", "OTHER"]).optional(),
    discountScope: z.enum(["FOOD", "ALL", "UNKNOWN"]).optional(),
});
/**
 * Per-field confidence (0..1) the model attached to drafted values, so the
 * client can highlight low-confidence cells for the user to check. All entries
 * optional — absence means "no signal".
 */
export const DraftConfidenceSchema = z.strictObject({
    label: z.number().min(0).max(1).optional(),
    currency: z.number().min(0).max(1).optional(),
    total: z.number().min(0).max(1).optional(),
});
/**
 * A translated copy of the drafted text, produced when the receipt is in a
 * language other than the group's target language (issue #284). Mirrors the
 * draft's `label` and item labels so the UI can show the original and the
 * translation side by side; money fields are not duplicated (numbers don't
 * translate). Absent when no translation was needed or possible.
 */
export const DraftTranslationSchema = z.strictObject({
    /** BCP-47 code the receipt was detected to be in (best-effort). */
    sourceLanguage: z.string().min(2).max(16).optional(),
    /** BCP-47 code the text was translated into (the group's language). */
    targetLanguage: z.string().min(2).max(16),
    /** Translated merchant/expense label, when the draft has one. */
    label: z.string().min(1).max(120).optional(),
    /** Translated item labels, index-aligned with `ExpenseDraft.items`. */
    items: z.array(z.string().min(1).max(200)).optional(),
});
/**
 * Best-effort expense draft. Every field is optional: the form renders with
 * whatever subset the pipeline produced and the user completes the rest. This
 * intentionally mirrors `CreateExpenseRequest` (label/currency/items) without
 * its `.min(1)` requirements.
 */
export const ExpenseDraftSchema = z.strictObject({
    label: z.string().min(1).max(120).optional(),
    currency: SupportedCurrencyCodesSchema.optional(),
    items: z.array(DraftItemSchema).optional(),
    /** Detected grand total in minor units, for reconciliation against items. */
    total: z.number().int().nonnegative().optional(),
    confidence: DraftConfidenceSchema.optional(),
    /**
     * Present when the receipt was translated into the group's language. The
     * original text stays in `label`/`items`; this carries the translated copy.
     */
    translation: DraftTranslationSchema.optional(),
});
/** Result returned by a completed receipt scan job. */
export const ScanResultSchema = z.strictObject({
    draft: ExpenseDraftSchema,
    /** Credits charged for this attempt. New attempts always report SCAN_COST. */
    creditsCharged: z.number().int().nonnegative(),
    remainingCredits: z.number().int().nonnegative(),
    /** SHA-256 of the raw OCR text, for support/debugging without storing PII. */
    rawTextHash: z.string().optional(),
    /**
     * Historical refund cause, retained so cached results created by the previous
     * policy remain readable during rollout. New scan results omit it.
     */
    refundReason: CreditRefundReasonSchema.optional(),
});
/** Body of `POST /groups/{groupId}/scan-jobs`. */
export const CreateScanJobRequestSchema = z.strictObject({
    objectKey: z.string().min(1),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    /** Server-issued by the upload ticket; one credit is charged per attempt. */
    attemptId: z.string().uuid().optional(),
});
export const ScanJobStageSchema = z.enum([
    "QUEUED",
    "READING_TEXT",
    "ANALYSING",
    "COMPLETE",
    "FAILED",
]);
/**
 * Durable, pollable receipt job. Results are retained briefly so selecting the
 * same receipt again can return immediately without repeating paid work.
 */
export const ScanJobSchema = z.strictObject({
    id: z.string().min(1),
    stage: ScanJobStageSchema,
    progress: z.number().int().min(0).max(100),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    result: ScanResultSchema.optional(),
    error: z.string().optional(),
    /** True when this request reused an existing in-flight/completed job. */
    reused: z.boolean().optional(),
});
